/**
 * @fileoverview Real-time WebSocket communication client for the Farmeando Aura Fan dynamic.
 * Manages WebSocket connection lifecycle, automatic reconnection, state synchronization,
 * side registration, and batched tap event dispatching.
 * 
 * @module utilities/WS/FarmeandoAuraFanWS
 */

let farmeandoAuraFanWebSocket = null;
let isFarmeandoAuraFanConnected = false;
let reconnectionTimeoutIdentifier = null;
let reconnectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 20;
const RECONNECTION_DELAY_MILLISECONDS = 2500;

/**
 * Establishes a real-time WebSocket connection for the Farmeando Aura fan mobile view.
 * 
 * @function ConnectWebSocketForFarmeandoAuraFan
 * @param {string} selectedSide - Team side chosen by the fan ('J1' or 'J2').
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked upon receiving parsed JSON packets.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked upon connection status changes.
 */
export const ConnectWebSocketForFarmeandoAuraFan = (
    selectedSide = 'J1',
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    if (farmeandoAuraFanWebSocket || isFarmeandoAuraFanConnected) {
        return;
    }

    const establishConnection = () => {
        if (reconnectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        reconnectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const normalizedSide = (selectedSide || 'J1').toLowerCase();
            const webSocketEndpointUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=overlay:farmeando-aura:fan:${normalizedSide}&side=${selectedSide || 'J1'}`;
            farmeandoAuraFanWebSocket = new WebSocket(webSocketEndpointUrl);

            farmeandoAuraFanWebSocket.onopen = () => {
                isFarmeandoAuraFanConnected = true;
                reconnectionAttemptCounter = 0;
                onStatusChange('connected');

                // Request initial snapshot of the game
                RequestFarmeandoAuraState();

                // Confirm side registration
                SendFarmeandoAuraJoinSide(selectedSide || 'J1');
            };

            farmeandoAuraFanWebSocket.onclose = () => {
                isFarmeandoAuraFanConnected = false;
                farmeandoAuraFanWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            farmeandoAuraFanWebSocket.onerror = () => {
                onStatusChange('error');
            };

            farmeandoAuraFanWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Ignore non-JSON or malformed packets
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a batch of accumulated taps to the backend real-time engine.
 * 
 * @function SendFarmeandoAuraTapBatch
 * @param {string} selectedSide - Team side identifier ('J1' or 'J2').
 * @param {number} tapCount - Number of accumulated taps to dispatch.
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const SendFarmeandoAuraTapBatch = (selectedSide = 'J1', tapCount = 1) => {
    try {
        if (
            farmeandoAuraFanWebSocket &&
            farmeandoAuraFanWebSocket.readyState === WebSocket.OPEN &&
            tapCount > 0
        ) {
            const tapBatchPayload = {
                type: 'aura_tap_batch',
                side: selectedSide,
                tapCount: tapCount
            };
            farmeandoAuraFanWebSocket.send(JSON.stringify(tapBatchPayload));
            return true;
        }
        return false;
    } catch (dispatchError) {
        return false;
    }
};

/**
 * Registers or switches the fan's chosen side on the server.
 * 
 * @function SendFarmeandoAuraJoinSide
 * @param {string} selectedSide - Team side identifier ('J1' or 'J2').
 * @param {string|null} [fanNickname=null] - Optional fan nickname.
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const SendFarmeandoAuraJoinSide = (selectedSide = 'J1', fanNickname = null) => {
    try {
        if (
            farmeandoAuraFanWebSocket &&
            farmeandoAuraFanWebSocket.readyState === WebSocket.OPEN
        ) {
            const joinPayload = {
                type: 'aura_join_side',
                side: selectedSide,
                nickname: fanNickname
            };
            farmeandoAuraFanWebSocket.send(JSON.stringify(joinPayload));
            return true;
        }
        return false;
    } catch (joinError) {
        return false;
    }
};

/**
 * Requests the current game state snapshot from the server.
 * 
 * @function RequestFarmeandoAuraState
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const RequestFarmeandoAuraState = () => {
    try {
        if (
            farmeandoAuraFanWebSocket &&
            farmeandoAuraFanWebSocket.readyState === WebSocket.OPEN
        ) {
            const requestStatePayload = {
                type: 'aura_get_state'
            };
            farmeandoAuraFanWebSocket.send(JSON.stringify(requestStatePayload));
            return true;
        }
        return false;
    } catch (requestError) {
        return false;
    }
};

/**
 * Cleanly disconnects and terminates the Farmeando Aura fan WebSocket connection.
 * 
 * @function DisconnectWebSocketForFarmeandoAuraFan
 */
export const DisconnectWebSocketForFarmeandoAuraFan = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (farmeandoAuraFanWebSocket) {
            farmeandoAuraFanWebSocket.onopen = null;
            farmeandoAuraFanWebSocket.onclose = null;
            farmeandoAuraFanWebSocket.onerror = null;
            farmeandoAuraFanWebSocket.onmessage = null;

            if (farmeandoAuraFanWebSocket.readyState === WebSocket.OPEN) {
                farmeandoAuraFanWebSocket.close();
            } else if (farmeandoAuraFanWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = farmeandoAuraFanWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            farmeandoAuraFanWebSocket = null;
            isFarmeandoAuraFanConnected = false;
            reconnectionAttemptCounter = 0;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};
